import { useEffect, useRef, useState } from 'react';

const MIN_KEYBOARD_SHRINK_REM = 8;

const viewportHeight = () =>
  window.visualViewport?.height || window.innerHeight;

const activeElementAcceptsText = () => {
  const activeElement = document.activeElement;
  if (!activeElement) return false;

  return (
    activeElement.tagName === 'INPUT' ||
    activeElement.tagName === 'TEXTAREA' ||
    activeElement.isContentEditable
  );
};

/**
 * Detects an on-screen keyboard without treating focus alone as sufficient.
 * The focus requirement avoids browser-toolbar resizes, while the visual
 * viewport shrink requirement avoids hiding navigation for hardware keyboards.
 */
export default function useSoftwareKeyboard() {
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const baselineHeightRef = useRef(null);
  const focusChangeTimeoutRef = useRef(null);
  const orientationTimeoutRef = useRef(null);

  useEffect(() => {
    const visualViewport = window.visualViewport;
    baselineHeightRef.current = viewportHeight();

    const updateKeyboardState = () => {
      const currentHeight = viewportHeight();
      const acceptsText = activeElementAcceptsText();
      const rootFontSize =
        Number.parseFloat(
          window.getComputedStyle(document.documentElement).fontSize
        ) || 16;
      const minimumKeyboardShrink =
        MIN_KEYBOARD_SHRINK_REM * rootFontSize;

      const updateOpenState = (nextIsOpen) => {
        isOpenRef.current = nextIsOpen;
        setIsOpen(nextIsOpen);
      };

      if (!acceptsText) {
        const keyboardShrink =
          baselineHeightRef.current - currentHeight;
        // Keep the navigation hidden while the keyboard animates closed.
        // Restoring it on blur alone makes it flash above the dismissing
        // keyboard for a frame on Android Chrome.
        if (isOpenRef.current && keyboardShrink >= minimumKeyboardShrink) {
          return;
        }
        baselineHeightRef.current = currentHeight;
        updateOpenState(false);
        return;
      }

      if (
        baselineHeightRef.current === null ||
        currentHeight > baselineHeightRef.current
      ) {
        baselineHeightRef.current = currentHeight;
      }

      const keyboardShrink =
        baselineHeightRef.current - currentHeight;

      updateOpenState(keyboardShrink >= minimumKeyboardShrink);
    };

    const handleFocusIn = () => {
      if (focusChangeTimeoutRef.current) {
        clearTimeout(focusChangeTimeoutRef.current);
      }
      updateKeyboardState();
    };

    const handleFocusOut = () => {
      if (focusChangeTimeoutRef.current) {
        clearTimeout(focusChangeTimeoutRef.current);
      }
      // Let focus settle before checking activeElement so switching between
      // fields does not briefly restore the navigation.
      focusChangeTimeoutRef.current = setTimeout(updateKeyboardState, 0);
    };

    const handleOrientationChange = () => {
      isOpenRef.current = false;
      setIsOpen(false);
      if (orientationTimeoutRef.current) {
        clearTimeout(orientationTimeoutRef.current);
      }
      orientationTimeoutRef.current = setTimeout(() => {
        baselineHeightRef.current = viewportHeight();
        updateKeyboardState();
      }, 250);
    };

    visualViewport?.addEventListener('resize', updateKeyboardState);
    window.addEventListener('resize', updateKeyboardState);
    window.addEventListener('orientationchange', handleOrientationChange);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      visualViewport?.removeEventListener('resize', updateKeyboardState);
      window.removeEventListener('resize', updateKeyboardState);
      window.removeEventListener('orientationchange', handleOrientationChange);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (focusChangeTimeoutRef.current) {
        clearTimeout(focusChangeTimeoutRef.current);
      }
      if (orientationTimeoutRef.current) {
        clearTimeout(orientationTimeoutRef.current);
      }
    };
  }, []);

  return isOpen;
}
